import { useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import QueryKeys from '@constants/queryKeys';
import {
  rateCompany,
  updatePaymentRateCompany,
} from '@worker/services/rating/api';
import IRateCompanyCreation from '@worker/types/rate-company-creation.type';
import { TodoResponse, TodoType } from '@th-types/inbox-reviews.type';
import { jobAssignmentKeys } from '@worker/hooks/useJobAssignment';

type RateCompanyMutationMode = 'CREATE' | 'PAYMENT_ONLY';

interface RateCompanyMutationVariables {
  jobAssignmentId: number;
  mode: RateCompanyMutationMode;
  body: IRateCompanyCreation;
}

const TODOS_QUERY_KEY = [QueryKeys.WorkerInbox.TODOS];
const INDICATORS_QUERY_KEY = [QueryKeys.WorkerInbox.INDICATORS];

const RATING_TODO_TYPES = new Set<TodoType>([
  TodoType.ENTER_RATING,
  TodoType.ENTER_RATING_PAYMENT,
]);

interface UseRateCompanyMutationOptions {
  onSuccess?: () => void;
}

export function useRateCompanyMutation(
  options?: UseRateCompanyMutationOptions
) {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, RateCompanyMutationVariables>({
    mutationFn: async ({ jobAssignmentId, mode, body }) => {
      const result =
        mode === 'CREATE'
          ? await rateCompany(jobAssignmentId, body)
          : await updatePaymentRateCompany(jobAssignmentId, {
              payment: body.payment,
            });

      if (!result || result.success === false) {
        throw new Error(
          result?.message || 'Could not submit your rating. Please try again.'
        );
      }
      return result;
    },

    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Could not submit rating',
        message: error.message,
      });
    },

    onSuccess: (_data, { jobAssignmentId }) => {
      queryClient.setQueryData<TodoResponse>(TODOS_QUERY_KEY, (prev) => {
        if (!prev) return prev;
        const nextList = prev.list.filter(
          (todo) =>
            !(
              todo.jobAssignmentId === jobAssignmentId &&
              RATING_TODO_TYPES.has(todo.type)
            )
        );
        if (nextList.length === prev.list.length) return prev;
        return {
          ...prev,
          count: nextList.length,
          list: nextList,
        };
      });

      queryClient.invalidateQueries({ queryKey: INDICATORS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: jobAssignmentKeys.detail(jobAssignmentId),
      });
      notifications.show({
        color: 'green',
        title: 'Rating sent',
        message: 'Thanks for sharing your feedback.',
      });
      options?.onSuccess?.();
    },
  });
}
